import { useLife } from '@/state/life';
import { useMarkets } from '@/state/markets';
import { usePower } from '@/state/power';
import { useXClone } from '@/state/xclone';
import { usePhone } from '@/state/phone';
import { useNotifications } from '@/state/notifications';
import { useCinematic } from '@/components/CinematicTakeover';
import { useMeta } from '@/state/meta';
import { sounds } from '@/audio/synth';
import { PHONE_ACTION_HANDLERS } from './phoneActions';

/**
 * Run-For-Office mini-arc.
 *
 * When notoriety crosses 80 the Senator floats it: "you should run."
 * Accepting fires a 4-step campaign with branching choices that determine
 * tribal coalition + outcome. Winner gets permanent influence +30. Loser
 * gets sanity -20 + ~$300K campaign debt.
 */

interface CampaignStep {
  text: string;
  options: { label: string; effect: () => number }[]; // effect returns a "campaign score" delta
}

const STEPS: CampaignStep[] = [
  {
    text: 'CAMPAIGN MANAGER: "first ad. what\'s the vibe?"',
    options: [
      { label: 'flag pin + walking through a factory', effect: () => { usePower.getState().bumpRep('maga', 12); return 2; } },
      { label: 'sleeves rolled up + diner counter', effect: () => { usePower.getState().bumpRep('left', 8); return 2; } },
      { label: 'AI-generated montage of yourself looking decisive', effect: () => { usePower.getState().bumpNotoriety(8); useLife.getState().bumpSanity(-2); return 1; } },
    ],
  },
  {
    text: 'DEBATE NIGHT. opponent attacks your trading record. how do you respond?',
    options: [
      { label: '"i made my money the old-fashioned way: i was lucky and well-connected."', effect: () => { usePower.getState().bumpRep('press', 6); return 2; } },
      { label: '"my opponent has never run a business and never met a real American."', effect: () => { usePower.getState().bumpRep('maga', 10); usePower.getState().bumpRep('left', -8); return 1; } },
      { label: '"i would like to redirect the question."', effect: () => { useLife.getState().bumpSanity(2); return 0; } },
    ],
  },
  {
    text: 'OPPOSITION RESEARCH leak: "your finsta from 2016." how do you handle?',
    options: [
      { label: 'preempt with a tearful Instagram apology', effect: () => { usePower.getState().bumpRep('press', 4); useLife.getState().bumpSanity(-4); return 2; } },
      { label: 'lean in. "yeah and i\'d post it again."', effect: () => { usePower.getState().bumpRep('wsb', 14); usePower.getState().bumpRep('cnbc', -8); return 1; } },
      { label: 'sue the journalist who reported it', effect: () => { useMarkets.getState().addCash(-40_000); usePower.getState().bumpRep('press', -12); return 0; } },
    ],
  },
  {
    text: 'ELECTION NIGHT. early returns are mixed. how do you spend the closing hour?',
    options: [
      { label: 'rally swing-state voters via Y', effect: () => { useXClone.getState().composePost('to every American watching this: today, you decide what kind of country we are. tomorrow, i\'ll forget i said this. WAGMI.'); return 2; } },
      { label: 'work the phones. call every donor.', effect: () => { useMarkets.getState().addCash(50_000); return 1; } },
      { label: 'go on Joe Rogan, again, live, while votes are being counted', effect: () => { usePower.getState().bumpNotoriety(15); return 1; } },
    ],
  },
];

let campaignScore = 0;

export function startRunForOfficeArc(): void {
  campaignScore = 0;
  useMeta.getState().recordLore('arc-run-for-office', 'Senate Campaign');
  useCinematic.getState().fire({
    id: `run-${Date.now()}`,
    type: 'breaking',
    headline: 'YOU\'RE RUNNING FOR SENATE.',
    subhead: '14 weeks of campaigning compressed into 4 decisions. democracy at work.',
    durationMs: 5500,
  });
  sounds.notifyAlert();

  for (let i = 0; i < STEPS.length; i++) {
    const s = STEPS[i]!;
    setTimeout(() => {
      const ids = ['a', 'b', 'c'];
      const actions = s.options.map((opt, idx) => ({
        id: ids[idx]!,
        label: opt.label,
        effectId: `run-q${i}-${ids[idx]}`,
      }));
      for (let j = 0; j < s.options.length; j++) {
        const id = `run-q${i}-${ids[j]}`;
        const opt = s.options[j]!;
        PHONE_ACTION_HANDLERS[id] = () => {
          campaignScore += opt.effect();
          sounds.fill();
        };
      }
      usePhone.getState().receiveMessage({
        threadId: 'senator',
        fromNpc: 'senator',
        text: s.text,
        actions,
      });
      useNotifications.getState().push({ surfaceId: 'phone', severity: 'urgent', text: `campaign: step ${i + 1}/4` });
      sounds.notifyAlert();
    }, 5_000 + i * 30_000);
  }

  // Election outcome
  setTimeout(() => {
    if (campaignScore >= 6) {
      usePower.getState().bumpInfluence(30);
      useLife.getState().bumpFbiHeat(-20); // immunity-ish
      useMarkets.getState().addCash(-200_000);
      useCinematic.getState().fire({
        id: `election-win-${Date.now()}`,
        type: 'ascension',
        headline: 'YOU WON THE SENATE SEAT.',
        subhead: 'congratulations, Senator. influence +30. campaign cost \$200K. ethics committee already opened a file.',
        durationMs: 6500,
      });
      useXClone.getState().receivePost({
        authorHandle: 'WSJ_RealTimeBait', authorTribe: 'press',
        text: 'BREAKING: a former retail trader has been elected to the United States Senate. analysts unsure how to feel. you are unsure how to feel. the trader is also unsure.',
        likes: 32000, reposts: 9800,
      });
    } else {
      useMarkets.getState().addCash(-300_000);
      useLife.getState().bumpSanity(-20);
      usePower.getState().bumpRep('press', -10);
      useCinematic.getState().fire({
        id: `election-loss-${Date.now()}`,
        type: 'breaking',
        headline: 'YOU LOST THE ELECTION.',
        subhead: 'campaign debt: \$300K. sanity: deeply impacted. opponent\'s concession speech somehow also dunked on you.',
        durationMs: 6500,
      });
    }
  }, 5_000 + STEPS.length * 30_000 + 5_000);
}
